import { getArrowHeadPoints } from "../geometry/arrowGeometry";
import { getMarkerJitter } from "./markerStroke";
import {
  getRoughArrowControls,
  getRoughArrowWing,
  getRoughDiamondVertices,
  getRoughEllipsePoints,
  getRoughRectangleCorners,
} from "./roughGeometry";

export { getElementSeed, strokeMarkerPath } from "./markerStroke";

const EDGE_BOW_MAX = 2.8;
const EDGE_BOW_RATIO = 0.008;

const getMidpoint = (from, to) => ({
  x: (from.x + to.x) / 2,
  y: (from.y + to.y) / 2,
});

const traceBowedEdge = ({ ctx, from, index, seed, to }) => {
  const deltaX = to.x - from.x;
  const deltaY = to.y - from.y;
  const edgeLength = Math.hypot(deltaX, deltaY);
  const bowLimit = Math.min(EDGE_BOW_MAX, edgeLength * EDGE_BOW_RATIO);
  const bow = getMarkerJitter({ index, scale: bowLimit * 2, seed });
  const midpoint = getMidpoint(from, to);
  const normalX = edgeLength ? -deltaY / edgeLength : 0;
  const normalY = edgeLength ? deltaX / edgeLength : 0;

  ctx.quadraticCurveTo(
    midpoint.x + normalX * bow,
    midpoint.y + normalY * bow,
    to.x,
    to.y
  );
};

const traceCorner = ({ corner, ctx }) => {
  ctx.bezierCurveTo(
    corner.controlOne.x,
    corner.controlOne.y,
    corner.controlTwo.x,
    corner.controlTwo.y,
    corner.end.x,
    corner.end.y
  );
};

/**
 * @param {{ ctx: CanvasRenderingContext2D, frame: object, passOffset?: number, seed: number }} params
 */
export const traceRoughRectangle = ({ ctx, frame, passOffset = 0, seed }) => {
  const corners = getRoughRectangleCorners({ frame, passOffset, seed });
  let currentPoint = corners[corners.length - 1].end;
  ctx.moveTo(currentPoint.x, currentPoint.y);
  corners.forEach((corner, index) => {
    traceBowedEdge({
      ctx,
      from: currentPoint,
      index: 40 + index + passOffset,
      seed,
      to: corner.start,
    });
    traceCorner({ corner, ctx });
    currentPoint = corner.end;
  });
  ctx.closePath();
};

/**
 * @param {{ ctx: CanvasRenderingContext2D, frame: object, passOffset?: number, seed: number }} params
 */
export const traceRoughDiamond = ({ ctx, frame, passOffset = 0, seed }) => {
  const vertices = getRoughDiamondVertices({ frame, passOffset, seed });
  ctx.moveTo(vertices[0].x, vertices[0].y);
  vertices.forEach((vertex, index) => {
    traceBowedEdge({
      ctx,
      from: vertex,
      index: 80 + index + passOffset,
      seed,
      to: vertices[(index + 1) % vertices.length],
    });
  });
  ctx.closePath();
};

/**
 * @param {{ ctx: CanvasRenderingContext2D, frame: object, passOffset?: number, seed: number }} params
 */
export const traceRoughEllipse = ({ ctx, frame, passOffset = 0, seed }) => {
  const points = getRoughEllipsePoints({ frame, passOffset, seed });
  const lastMidpoint = getMidpoint(points[points.length - 1], points[0]);
  ctx.moveTo(lastMidpoint.x, lastMidpoint.y);
  points.forEach((point, index) => {
    const nextPoint = points[(index + 1) % points.length];
    const nextMidpoint = getMidpoint(point, nextPoint);
    ctx.quadraticCurveTo(point.x, point.y, nextMidpoint.x, nextMidpoint.y);
  });
  ctx.closePath();
};

/**
 * @param {{ ctx: CanvasRenderingContext2D, passOffset?: number, points: object, seed: number }} params
 */
export const traceRoughArrowShaft = ({ ctx, passOffset = 0, points, seed }) => {
  const { controlOne, controlTwo } = getRoughArrowControls({
    passOffset,
    points,
    seed,
  });
  ctx.moveTo(points.x1, points.y1);
  ctx.bezierCurveTo(
    controlOne.x,
    controlOne.y,
    controlTwo.x,
    controlTwo.y,
    points.x2,
    points.y2
  );
};

/**
 * @param {{ ctx: CanvasRenderingContext2D, passOffset?: number, points: object, seed: number }} params
 */
export const traceRoughArrowHead = ({ ctx, passOffset = 0, points, seed }) => {
  const { leftWing, rightWing, tip } = getArrowHeadPoints(points);
  const roughLeft = getRoughArrowWing({
    index: 0,
    passOffset,
    seed,
    wing: leftWing,
  });
  const roughRight = getRoughArrowWing({
    index: 1,
    passOffset,
    seed,
    wing: rightWing,
  });

  ctx.moveTo(roughLeft.x, roughLeft.y);
  traceBowedEdge({ ctx, from: roughLeft, index: 150 + passOffset, seed, to: tip });
  traceBowedEdge({ ctx, from: tip, index: 151 + passOffset, seed, to: roughRight });
};
